import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Keyboard, Pagination, Mousewheel, Navigation } from "swiper/modules";
import Iconify from "@/components/ui/Iconify";
import { CLOUDFRONT_BASE_URL } from "../../../../config";
import LivePhotoPlayer from "@/components/ui/LivePhotoPlayer";
import { getMediaRecordUrl } from "@/utils/media";

const getSlideImageUrl = (slide) =>
  getMediaRecordUrl(slide, { variant: "large" });

const FullScreenImageSlider = ({ isOpen, setIsOpen, slides }) => {
  const fullScreenSwiperRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const closeFullscreen = () => setIsOpen(false);
  const handleBackdropClick = (event) => {
    const controlTarget = event.target?.closest?.(
      "[data-fullscreen-image-control='true'], .swiper-pagination, .custom-prev-fullscreen, .custom-next-fullscreen"
    );

    if (controlTarget) return;
    closeFullscreen();
  };

  const stopBackdropClose = (event) => {
    event.stopPropagation();
  };

  return (
    <div
      className={`${
        isOpen ? "fixed" : "hidden"
      } inset-0 z-[99] h-[100dvh] w-screen bg-black/80 backdrop-blur-lg`}
    >
      <div className="w-full h-full relative group" onClick={handleBackdropClick}>
        <Swiper
          ref={fullScreenSwiperRef}
          style={{
            "--swiper-navigation-color": "#fff",
            "--swiper-pagination-color": "#fff",
          }}
          onBeforeInit={(swiper) => {
            fullScreenSwiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex + 1)} // Update current slide on change
          slidesPerView={1}
          spaceBetween={30}
          keyboard={{
            enabled: true,
          }}
          mousewheel={true}
          pagination={{
            clickable: true,
          }}
          lazy="true"
          navigation={{
            prevEl: ".custom-prev-fullscreen",
            nextEl: ".custom-next-fullscreen",
          }}
          modules={[Keyboard, Mousewheel, Pagination, Navigation]}
          className="h-full w-full"
        >
          {slides?.map((slide, index) => (
            <SwiperSlide
              key={index}
              className="!flex h-full w-full items-center justify-center px-4 py-12"
            >
              {slide?.mediaType === "livephoto" ? (
                <div
                  className="relative z-[1] flex h-full w-full items-center justify-center pointer-events-auto"
                  data-fullscreen-image-control="true"
                  onClick={stopBackdropClose}
                >
                  <LivePhotoPlayer
                    photoUrl={
                      slide?.livePhotoImagePath?.startsWith("uploads/")
                        ? `${CLOUDFRONT_BASE_URL}/${slide?.livePhotoImagePath}`
                        : slide?.livePhotoImagePath
                    }
                    videoUrl={
                      slide?.livePhotoVideoPath?.startsWith("uploads/")
                        ? `${CLOUDFRONT_BASE_URL}/${slide?.livePhotoVideoPath}`
                        : slide?.livePhotoVideoPath
                    }
                  />
                </div>
              ) : (
                <img
                  src={getSlideImageUrl(slide)}
                  alt="slide"
                  className="block max-h-full max-w-full h-auto w-auto object-contain cursor-zoom-out"
                  onClick={closeFullscreen}
                  draggable={false}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Pagination */}
        <div
          className={` ${slides?.length > 1 ? "flex" : "hidden"
            } invisible group-hover:visible justify-center items-center rounded-full bg-black bg-opacity-15 py-1 px-2 absolute top-5 lg:top-8 right-7 lg:right-5 transform -translate-y-1/2 text-white z-[75]`}
          data-fullscreen-image-control="true"
          onClick={stopBackdropClose}
        >
          <span className={"!text-dark_secondary_label text-xs"}>
            {currentSlide}/{slides?.length}
          </span>
        </div>
        {/* Close Icon */}
        <div
          className="invisible group-hover:visible absolute bg-black bg-opacity-15 py-1 px-1 top-5 lg:top-8 left-7 lg:left-5 flex items-center justify-center rounded-full cursor-pointer z-[80]"
          data-fullscreen-image-control="true"
          onClick={closeFullscreen}
        >
          <Iconify
            icon={"iconamoon:close-thin"}
            className="!w-6 !h-6 !text-dark_secondary_label"
          />
        </div>
        {/* Custom navigation buttons */}
        <div
          className={`${
            slides?.length > 1 ? "flex" : "hidden"
          } custom-prev-fullscreen invisible group-hover:visible absolute left-3 md:left-8 top-1/2 h-11 w-11 -translate-y-1/2 items-center justify-center z-[75] cursor-pointer rounded-full bg-black/25 backdrop-blur`}
          data-fullscreen-image-control="true"
          onClick={stopBackdropClose}
        >
          <button className="flex h-full w-full items-center justify-center rounded-full">
            <Iconify
              icon={"iconamoon:arrow-left-2-duotone"}
              className="!w-6 !h-6 !text-dark_secondary_label"
            />
          </button>
        </div>
        <div
          className={`${
            slides?.length > 1 ? "flex" : "hidden"
          } custom-next-fullscreen invisible group-hover:visible absolute right-3 md:right-8 top-1/2 h-11 w-11 -translate-y-1/2 items-center justify-center z-[75] cursor-pointer rounded-full bg-black/25 backdrop-blur`}
          data-fullscreen-image-control="true"
          onClick={stopBackdropClose}
        >
          <button className="flex h-full w-full items-center justify-center rounded-full">
            <Iconify
              icon={"iconamoon:arrow-right-2-duotone"}
              className="!w-6 !h-6 !text-dark_secondary_label"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FullScreenImageSlider;
